import { Box } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useRoleColors } from "../../../hooks/useRoleColors";
import type { NurseRole } from "../../../utils/models";
import { nurseRoleDisplayName, rolesMap } from "../../../utils/utilFunctions";
import { type SystemChipItem, SystemChips } from "../Clickable/TabsAndChips/SystemChips/SystemChips";

export const RoleChips = ({
	onSelectedRoleFiltersChanged,
	initiallySelected,
	minimumOneSelected = false,
	hideAllOption = false,
	availableRoles,
	disabled,
}: {
	onSelectedRoleFiltersChanged: (selected: (NurseRole | "all")[]) => void;
	initiallySelected?: (NurseRole | "all")[];
	minimumOneSelected?: boolean;
	hideAllOption?: boolean;
	availableRoles?: NurseRole[];
	/** Show the chips as they are but take no input (a read-only form). Absent = interactive, as today. */
	disabled?: boolean;
}) => {
	const roleColor = useRoleColors();
	const { chosenDepartment } = useChosenDepartment();
	const departmentSpecificRoles = availableRoles ?? chosenDepartment.roles;
	const allValueForChips = useMemo<SystemChipItem<"all">>(() => ({ value: "all", label: "All" }), []);
	const roleFilterChips: SystemChipItem<NurseRole | "all">[] = useMemo(
		() => [
			...(hideAllOption ? [] : [allValueForChips]),
			// Keep only real roles: an unmapped token (e.g. coverage's "all" wildcard) has no color and would
			// render as a grey dot, and RoleChips already renders its own "All" chip.
			...departmentSpecificRoles
				.filter((value) => value in rolesMap)
				.map((value) => ({
					value,
					label: nurseRoleDisplayName[value] ?? value,
					startAdornment: (
						<Box
							style={{
								borderRadius: "50%",
								backgroundColor: roleColor(rolesMap[value]).dot,
							}}
							sx={{
								width: "10px !important",
								height: "10px !important",
							}}
						/>
					),
				})),
		],
		[departmentSpecificRoles, allValueForChips, hideAllOption, roleColor],
	);
	const allRoleChips = useMemo(() => roleFilterChips.filter((c) => c.value !== "all"), [roleFilterChips]);

	const [selectedRoleFilters, setSelectedRoleFilters] = React.useState<SystemChipItem<NurseRole | "all">[]>(() => {
		if (!initiallySelected) return [allValueForChips];
		if (initiallySelected.includes("all")) return [allValueForChips];
		const matched = roleFilterChips.filter((value) => initiallySelected.includes(value.value));
		if (!hideAllOption && matched.length === allRoleChips.length && allRoleChips.length > 0) return [allValueForChips];
		return matched;
	});

	const handleRoleFilterChange = useCallback(
		(value: SystemChipItem<NurseRole | "all">, selected: boolean) => {
			let updatedSelectedRoleFilters = [...selectedRoleFilters];
			if (hideAllOption) {
				if (selected) {
					updatedSelectedRoleFilters = [...updatedSelectedRoleFilters, value];
				} else {
					updatedSelectedRoleFilters = updatedSelectedRoleFilters.filter((item) => item.value !== value.value);
				}
			} else if (value.value !== "all") {
				if (selected) {
					updatedSelectedRoleFilters = [...updatedSelectedRoleFilters.filter((val) => val.value !== "all"), value];
					if (updatedSelectedRoleFilters.length === allRoleChips.length) {
						updatedSelectedRoleFilters = [allValueForChips];
					}
				} else {
					if (!minimumOneSelected || updatedSelectedRoleFilters.length > 1) {
						updatedSelectedRoleFilters = updatedSelectedRoleFilters.filter((item) => item.value !== value.value);
					}
				}
			} else {
				updatedSelectedRoleFilters = [allValueForChips];
			}
			setSelectedRoleFilters(updatedSelectedRoleFilters);
			const emittedValues = updatedSelectedRoleFilters.map((v) => v.value);
			if (emittedValues.includes("all")) {
				onSelectedRoleFiltersChanged(allRoleChips.map((c) => c.value));
			} else {
				onSelectedRoleFiltersChanged(emittedValues);
			}
		},
		[allValueForChips, onSelectedRoleFiltersChanged, selectedRoleFilters, allRoleChips, minimumOneSelected, hideAllOption],
	);

	// Every chip off at once when the whole set is read-only; otherwise the chips as they have always been.
	const chips = useMemo(() => (disabled ? roleFilterChips.map((chip) => ({ ...chip, disabled: true })) : roleFilterChips), [disabled, roleFilterChips]);

	return (
		<SystemChips items={chips} onChange={handleRoleFilterChange} selectedItems={selectedRoleFilters} forceFitContent={true} dataTestId="role-chips-container" />
	);
};
